import { PaginationInfo } from "src/components/tailwind-components/Pagination"

export type TemplateDocumentType = "none" | "have"
export type BusinessFormatType = "only_physical" | "other"

// テーブル関連
export interface PartnerPreRegistrationTableProps {
  data: PartnerPreRegistrationSummary[]
  isLoading?: boolean
}

export interface PartnerPreRegistrationTableRowProps {
  partnerPreRegistration: PartnerPreRegistrationSummary
}

// フォーム関連
export interface PartnerPreRegistrationFormProps {
  data: PartnerPreRegistrationDetail
  onSubmit: (data: PartnerPreRegistrationDetail, nextStatus: PartnerPreRegistrationStatus) => void
}

export type PartnerPreRegistrationStatus =
  | "in_review"
  | "approved"
  | "rejected"
  | "in_compliance_review"
  | "revoked"
  | "checking"

// セクション別コンポーネント用
export interface FormSectionProps {
  data: PartnerPreRegistrationDetail
  onChange: (field: keyof PartnerPreRegistrationDetail, value: any) => void
}

// 一覧の各行データ
export interface PartnerPreRegistrationSummary {
  id: number
  companyName: string // バックエンドからcamelCaseで返される
  status: PartnerPreRegistrationStatus
  statusName: string
  createdAt: string
}

/** パートナー事前登録取得のパラメータ */
export interface UseGetPartnerPreRegistrationsParams {
  statuses?: string[]
  page?: number
}

// APIレスポンス型
export interface PartnerPreRegistrationsApiResponse {
  partnerPreRegistrations: PartnerPreRegistrationSummary[]
  pagination: PaginationInfo
}

export interface UseGetPartnerPreRegistrationsReturn {
  partnerPreRegistrations: PartnerPreRegistrationSummary[]
  /** ページネーション情報 */
  pagination?: PaginationInfo
  data?: PartnerPreRegistrationsApiResponse
  isLoading: boolean
  error: Error | null
  isEmpty: boolean
  mutate: () => Promise<any>
  refresh: () => Promise<any>
}

// 代表者情報の型定義
export interface President {
  id: number | undefined
  seiName: string
  meiName: string
  kanaSeiName: string
  kanaMeiName: string
  birthday: string
  jdmFileUrl: string | null
  csrsFileUrl: string | null
}

// 詳細情報用の型定義（showエンドポイント用）
export interface PartnerPreRegistrationDetail {
  id: number
  status: PartnerPreRegistrationStatus
  statusName: string
  companyName: string
  companyKanaName: string
  presidents: President[]
  companyPostcode: string
  companyAddress: string
  companyPhoneNumber: string
  companyFaxNumber: string | null
  businessFormat: BusinessFormatType
  templateDocument: TemplateDocumentType
  corporateNumber: string
  realEstateNumber: string
  note: string
  updatedAt: string
  corporateRegistryAcquisitionResult: AutoAcquisitionResult<CorporateRegistryResult> | null
  realEstateNumberAcquisitionResult: AutoAcquisitionResult<RealEstateNumberResultItem[]> | null
  reputationCheckAcquisitionResult: AutoAcquisitionResult<ReputationCheckResult> | null
  corporateRegistryFileUrl: string | null
  jdmFileUrl: string | null
  csrsFileUrl: string | null
  checkedTypes?: string[]
}

export type CorporateRegistryAcquisitionStatus = "on_going" | "completed" | "failed"

// APIレスポンス型（詳細情報）
export interface PartnerPreRegistrationDetailApiResponse {
  partnerPreRegistration: PartnerPreRegistrationDetail
}

// Hook戻り値型（詳細情報取得用）
export interface UseGetPartnerPreRegistrationReturn {
  partnerPreRegistration: PartnerPreRegistrationDetail | null
  data?: PartnerPreRegistrationDetailApiResponse
  isLoading: boolean
  error: Error | null
  mutate: () => Promise<any>
  refresh: () => Promise<any>
}

// Update関連の型定義
export interface UpdatePartnerPreRegistrationRequest {
  status?: PartnerPreRegistrationStatus
  companyName?: string
  companyKanaName?: string
  presidents?: President[]
  companyPostcode?: string
  companyAddress?: string
  companyPhoneNumber?: string
  companyFaxNumber?: string | null
  businessFormat?: BusinessFormatType
  templateDocument?: TemplateDocumentType
  corporateNumber?: string
  realEstateNumber?: string
  note?: string
  checkedTypes?: string[]
}

// APIレスポンス型（更新用）
export interface UpdatePartnerPreRegistrationApiResponse {
  partnerPreRegistration: PartnerPreRegistrationDetail
  message: string
}

// エラーレスポンス型
export interface UpdatePartnerPreRegistrationErrorResponse {
  errors?: Record<string, string[]>
  message: string
  error?: string
}

// Hook戻り値型（更新用）
export interface UseUpdatePartnerPreRegistrationReturn {
  updatePartnerPreRegistration: (
    id: number,
    data: UpdatePartnerPreRegistrationRequest,
    nextStatus: PartnerPreRegistrationStatus,
    checklistStatuses: CheckListStatus[]
  ) => Promise<UpdatePartnerPreRegistrationApiResponse>
  isUpdating: boolean
  error: Error | null
  reset: () => void
}

export type ChecklistItemId =
  | "registry_obtained"
  | "president_count"
  | "president_names"
  | "reputation_check_ok"

export interface CheckListStatus {
  id: ChecklistItemId
  checked: boolean
}

export interface Checklist {
  id: ChecklistItemId
  text: string
}

export type AcquisitionType = "corporate_registry" | "real_estate_number" | "reputation_check"
export type CheckType = "kycc" | "jdm_csrs" | "all"
export type ReputationCheckTarget = "company" | "president"

// StartAcquisition関連の型定義
export interface StartAcquisitionRequest {
  partnerPreRegistrationId: number
  acquisitionType: AcquisitionType
  checkType?: CheckType
  target?: ReputationCheckTarget
  targetPresidentId?: number
}

// エラーレスポンス型（法人登記取得開始用）
export interface StartAcquisitionErrorResponse {
  errors?: Record<string, string[]>
  message: string
  error?: string
}

// Hook戻り値型（法人登記取得開始用）
export interface UseStartAcquisitionReturn {
  startAcquisition: (request: StartAcquisitionRequest) => Promise<void>
  isStarting: boolean
  error: Error | null
  reset: () => void
}

export type AutoAcquisitionStatus = "on_going" | "completed" | "failed" | "not_started"

export interface AutoAcquisitionResult<T> {
  status: AutoAcquisitionStatus
  result: T
  pdfUrl?: string
  acquiredAt: string
  updatedAt: string
  errorMessage: string
}

export interface CheckAcquisitionApiResponse {
  autoAcquisitionResult: AutoAcquisitionResult<
    RealEstateNumberResultItem[] | CorporateRegistryResult | ReputationCheckResult
  > | null
}

export interface RealEstateNumberResultItem {
  realEstateNumber: string
  companyName: string
  companyAddress: string
}

export interface CorporateRegistryResult {
  s3_key: string
}

export interface ReputationCheckResult {
  company: CompanyReputationCheckResult | undefined
  // presidentsは、代表はIDがkeyで、PresidentReputationCheckResultがvalueのオブジェクト
  presidents: Record<number, PresidentReputationCheckResult>
}

export interface CompanyReputationCheckResult {
  kycc: KyccResult | undefined
}

export interface PresidentReputationCheckResult {
  kycc: KyccResult | undefined
  jdmCsrs: JdmCsrsResult | undefined
}

export interface KyccResult {
  status: AutoAcquisitionStatus
  result: ReputationCheckExecuteResult
  errorMessage: string
  url: string
  completedAt: string
}

export interface JdmCsrsResult {
  status: AutoAcquisitionStatus
  completedAt: string
  jdm: {
    result: ReputationCheckExecuteResult
    url: string
    errorMessage: string
  }
  csrs: {
    result: ReputationCheckExecuteResult
    url: string
    errorMessage: string
  }
}

export type ReputationCheckExecuteResult = "ok" | "ng" | "error"

// AutoAcquisitionAccount関連の型定義
export type AccountType = "jdm" | "kycc" | "jdm_zip" | "touki"

export interface AutoAcquisitionAccount {
  id: number
  loginId: string
  accountType: AccountType
  name: string
}

export interface AutoAcquisitionAccountsApiResponse {
  autoAcquisitionAccounts: AutoAcquisitionAccount[]
}

export interface UseGetAutoAcquisitionAccountsReturn {
  autoAcquisitionAccounts: AutoAcquisitionAccount[]
  isLoading: boolean
  error: Error | null
  mutate: () => Promise<any>
  refresh: () => Promise<any>
}

// AccountTypeの表示名変換用
export const AccountTypeLabels: Record<AccountType, string> = {
  jdm: "JDM",
  kycc: "KYCC",
  jdm_zip: "JDM(ZIPファイル展開用)",
  touki: "登記簿",
}

// パスワード更新関連
export interface UpdateAutoAcquisitionAccountPasswordRequest {
  password?: string
  name?: string
}

export interface UpdateAutoAcquisitionAccountPasswordApiResponse {
  message: string
}

export interface UseUpdateAutoAcquisitionAccountPasswordReturn {
  updateAccount: (id: number, password?: string, name?: string) => Promise<void>
  isUpdating: boolean
  error: Error | null
}
